import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Loader2, Upload, Sparkles, Search, FileText, ShieldCheck, AlertTriangle, Trash2,
  ScanText, X,
} from "lucide-react";

const DOC_TYPES = ["Requisition", "Gross worksheet", "Outside report", "Consent",
                   "Correspondence", "QC / instrument log", "Frozen section worksheet",
                   "Accession log", "Other"];
const blank = { title: "", doc_type: "Requisition", case_id: "", patient_reference: "",
                patient_name: "", date_on_document: "", notes: "" };

const VERIFY_TONE = {
  Verified: "bg-emerald-100 text-emerald-900",
  Unverified: "bg-amber-100 text-amber-900",
  "Needs correction": "bg-red-100 text-red-900",
};

const Stat = ({ label, value, tone, testid }) => (
  <div className="border border-slate-200 bg-white p-4 dark:border-slate-700" data-testid={testid}>
    <p className={`font-display text-3xl font-bold ${tone || "text-[#134e4a]"}`}>{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);

export default function Archive() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [working, setWorking] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [params, setParams] = useSearchParams();
  const [linkedAccession, setLinkedAccession] = useState("");

  const canVerify = ["admin", "lab_director", "pathologist", "pathologist_assistant",
                     "supervisor", "transcriptionist"].includes(user?.role);
  const canDelete = ["admin", "lab_director", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get("/archive/documents", { params: { q, scope } }),
        api.get("/archive/stats"),
      ]);
      setRows(list.data);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [q, scope]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  // Arriving from a case page: pre-link the case and open the upload form.
  useEffect(() => {
    const caseId = params.get("case_id");
    if (!caseId) return;
    setForm({ ...blank, case_id: caseId });
    setLinkedAccession(params.get("accession") || "");
    setOpen(true);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const openDoc = async (id) => {
    try {
      const { data } = await api.get(`/archive/documents/${id}`);
      setActive(data);
      setDraft(data.transcript || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, case_id: form.case_id || null };
      const { data } = await api.post("/archive/documents", body);
      toast.success("Archive entry created — add the scanned pages next");
      setOpen(false);
      setForm(blank);
      setActive(data);
      setDraft("");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addPages = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active) return;
    setWorking("upload");
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/archive/documents/${active.id}/pages`, body,
                                      { timeout: 300000 });
      setActive(data);
      toast.success(`${file.name} added`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setWorking(""); }
  };

  const transcribe = async () => {
    setWorking("ocr");
    try {
      const { data } = await api.post(`/archive/documents/${active.id}/transcribe`, {},
                                      { timeout: 600000 });
      setActive(data);
      setDraft(data.transcript || "");
      toast.success("Transcribed — please read it against the original before verifying");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setWorking(""); }
  };

  const saveTranscript = async (verification) => {
    setWorking("save");
    try {
      const { data } = await api.put(`/archive/documents/${active.id}/transcript`,
                                     { text: draft, verification });
      setActive(data);
      toast.success(verification ? `Marked ${verification}` : "Transcript saved");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setWorking(""); }
  };

  const setRetention = async (years) => {
    setWorking("retention");
    try {
      const { data } = await api.put(`/archive/documents/${active.id}/retention`,
                                     { retention_years: Number(years), destroy_on: "" });
      setActive(data);
      toast.success(data.destroy_on ? `Eligible for destruction ${data.destroy_on}`
                                    : "Retained indefinitely");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setWorking(""); }
  };

  const logDestruction = async () => {
    const method = window.prompt("How was the paper destroyed?", "Cross-cut shredding");
    if (!method) return;
    const witness = window.prompt("Witnessed by (optional)", "") || "";
    setWorking("destroy");
    try {
      const { data } = await api.post(`/archive/documents/${active.id}/destroy`,
                                      { method, witness });
      setActive(data);
      toast.success(`Destruction logged — certificate ${data.destruction.certificate_number}`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setWorking(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document from the archive? The scans are deleted too.")) return;
    try {
      await api.delete(`/archive/documents/${id}`);
      setActive(null);
      toast.success("Removed from the archive");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const ask = async (e) => {
    e.preventDefault();
    setWorking("ask");
    setAnswer(null);
    try {
      const { data } = await api.post("/archive/ask", { question }, { timeout: 180000 });
      setAnswer(data);
    } catch (err) { toast.error(apiError(err)); } finally { setWorking(""); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="archive-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            ArchivIQ&#8209;Med — Document Archive
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Scan paper results and handwritten records, have them read by AI, then verify and
            search them. Works for current cases and for older paper files.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-archive-button"
                className="ml-auto bg-[#134e4a] hover:bg-[#0f3f3c]">
          <Plus size={16} className="mr-2" /> Archive a document
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Documents" value={stats.total ?? 0} testid="archive-stat-total" />
        <Stat label="Linked to cases" value={stats.case_linked ?? 0} testid="archive-stat-case" />
        <Stat label="Legacy paper files" value={stats.legacy ?? 0} testid="archive-stat-legacy"
              tone="text-slate-700" />
        <Stat label="Awaiting verification" value={stats.unverified ?? 0}
              testid="archive-stat-unverified" tone="text-amber-700" />
        <Stat label="Due for destruction" value={stats.due_destruction ?? 0}
              testid="archive-stat-due" tone="text-red-700" />
      </div>

      <section className="border border-slate-200 bg-white p-4 dark:border-slate-700">
        <form onSubmit={ask} className="flex flex-wrap items-center gap-2">
          <Sparkles size={16} className="text-[#134e4a]" />
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                 data-testid="archive-ask-input" className="min-w-[16rem] flex-1"
                 placeholder="Ask the archive: “which 2019 frozen section logs mention Dr Alvarez?”" />
          <Button type="submit" disabled={working === "ask" || question.trim().length < 3}
                  data-testid="archive-ask-button" className="bg-[#134e4a] hover:bg-[#0f3f3c]">
            {working === "ask" ? <Loader2 size={15} className="mr-2 animate-spin" /> : null}
            Ask
          </Button>
        </form>
        {answer && (
          <div className="mt-3 border-l-4 border-[#134e4a] bg-slate-50 p-3 dark:bg-slate-800"
               data-testid="archive-answer">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
              {answer.answer}
            </p>
            {!!answer.sources?.length && (
              <p className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                {answer.sources.map((s, i) => (
                  <button key={s.id} onClick={() => openDoc(s.id)}
                          data-testid={`archive-source-${s.id}`}
                          className="underline-offset-2 hover:text-[#134e4a] hover:underline">
                    [{i + 1}] {s.title}
                  </button>
                ))}
              </p>
            )}
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <Search size={15} className="absolute left-3 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                 data-testid="archive-search-input"
                 placeholder="Search titles, transcribed text, patient reference, accession…" />
        </div>
        {[["", "All"], ["case", "Case-linked"], ["legacy", "Legacy paper"],
          ["due", "Due for destruction"], ["destroyed", "Destroyed"]].map(([key, label]) => (
          <button key={key || "all"} onClick={() => setScope(key)}
                  data-testid={`archive-scope-${key || "all"}`}
                  className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                    scope === key ? "border-[#134e4a] bg-[#134e4a] text-white"
                      : "border-slate-300 text-slate-600 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <Loader2 className="animate-spin text-slate-400" size={20} />}

      {!loading && rows.length === 0 && (
        <p className="border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500"
           data-testid="archive-empty">
          Nothing archived yet. Scan a requisition, an old log sheet or a handwritten worksheet to
          begin.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((r) => (
          <article key={r.id} data-testid={`archive-row-${r.id}`}
                   className="border border-slate-200 bg-white p-4 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => openDoc(r.id)} data-testid={`archive-open-${r.id}`}
                      className="text-left font-display text-base font-bold hover:text-[#134e4a]">
                {r.title}
              </button>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  VERIFY_TONE[r.verification] || VERIFY_TONE.Unverified}`}>
                {r.verification}
              </span>
              <span className="ml-auto text-xs text-slate-500">
                {r.page_count} page{r.page_count === 1 ? "" : "s"}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {r.doc_type}
              {r.accession_number ? ` · ${r.accession_number}` : " · legacy paper"}
              {r.patient_reference ? ` · ${r.patient_reference}` : ""}
              {r.date_on_document ? ` · ${r.date_on_document}` : ""}
            </p>
            {r.excerpt && (
              <p className="mt-2 line-clamp-2 border-l-2 border-slate-200 pl-3 text-sm italic text-slate-600 dark:text-slate-400">
                {r.excerpt}
              </p>
            )}
          </article>
        ))}
      </div>

      {/* create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
                       data-testid="archive-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Archive a document</DialogTitle>
            <DialogDescription>
              Give it a title and, if it belongs to a case in PathQity, paste that case ID. Leave
              the case blank for older paper files.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label htmlFor="a-title">Title</Label>
              <Input id="a-title" required value={form.title} onChange={set("title")}
                     data-testid="archive-title-input"
                     placeholder="1998 frozen section log, January–March" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="a-type">Document type</Label>
                <select id="a-type" value={form.doc_type} onChange={set("doc_type")}
                        data-testid="archive-type-select"
                        className="mt-1 h-10 w-full border border-slate-300 bg-white px-3 text-sm dark:bg-slate-900">
                  {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="a-date">Date on the document</Label>
                <Input id="a-date" value={form.date_on_document} onChange={set("date_on_document")}
                       data-testid="archive-date-input" placeholder="12 March 1998" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="a-ref">Patient reference</Label>
                <Input id="a-ref" value={form.patient_reference} onChange={set("patient_reference")}
                       data-testid="archive-ref-input" placeholder="MRN or old accession" />
              </div>
              <div>
                <Label htmlFor="a-case">PathQity case ID (optional)</Label>
                <Input id="a-case" value={form.case_id} onChange={set("case_id")}
                       data-testid="archive-case-input" placeholder="Leave blank for legacy paper" />
                {linkedAccession && form.case_id && (
                  <p className="mt-1 text-xs font-semibold text-[#134e4a]"
                     data-testid="archive-linked-case-note">
                    Pre-linked to case {linkedAccession}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="a-notes">Notes</Label>
              <Textarea id="a-notes" rows={2} value={form.notes} onChange={set("notes")}
                        data-testid="archive-notes-input"
                        placeholder="Where the original is filed, box number, condition…" />
            </div>
            <Button type="submit" disabled={busy} data-testid="archive-create-submit"
                    className="w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Create and add pages
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* read / verify */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto"
                       data-testid="archive-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{active.title}</DialogTitle>
                <DialogDescription>
                  {active.doc_type}
                  {active.accession_number ? ` · case ${active.accession_number}` : " · legacy paper file"}
                  {active.date_on_document ? ` · ${active.date_on_document}` : ""}
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    VERIFY_TONE[active.verification] || VERIFY_TONE.Unverified}`}
                      data-testid="archive-verification-pill">
                  {active.verification}
                </span>
                {active.verified_by && (
                  <span className="text-xs text-slate-500">by {active.verified_by}</span>
                )}
                {active.destroyed ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/archive/documents/${active.id}/certificate.pdf`}
                     target="_blank" rel="noreferrer" data-testid="archive-certificate-link"
                     className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2.5 py-0.5 text-xs font-semibold text-white">
                    <FileText size={11} /> Paper destroyed — certificate
                  </a>
                ) : (
                  <span className="text-xs text-slate-500" data-testid="archive-retention-label">
                    Retain {active.retention_years ?? 0} yr
                    {active.destroy_on ? ` · eligible ${active.destroy_on}` : " · no destruction date"}
                  </span>
                )}
                <input ref={fileRef} type="file" hidden onChange={addPages}
                       accept="image/jpeg,image/png,application/pdf"
                       data-testid="archive-file-input" />
                <Button size="sm" onClick={() => fileRef.current?.click()}
                        disabled={!!working} data-testid="archive-add-pages-button"
                        className="ml-auto bg-slate-700 hover:bg-slate-800">
                  {working === "upload" ? <Loader2 size={14} className="mr-2 animate-spin" />
                    : <Upload size={14} className="mr-2" />}
                  Add scan or PDF
                </Button>
                <Button size="sm" onClick={transcribe}
                        disabled={!!working || !active.page_count}
                        data-testid="archive-transcribe-button"
                        className="bg-[#134e4a] hover:bg-[#0f3f3c]">
                  {working === "ocr" ? <Loader2 size={14} className="mr-2 animate-spin" />
                    : <ScanText size={14} className="mr-2" />}
                  {working === "ocr" ? "Reading…" : "Read with AI"}
                </Button>
                {canVerify && !active.destroyed && (
                  <select value={active.retention_years ?? 0} disabled={!!working}
                          onChange={(e) => setRetention(e.target.value)}
                          data-testid="archive-retention-select"
                          className="h-9 border border-slate-300 bg-white px-2 text-xs dark:bg-slate-900">
                    {[0, 2, 5, 7, 10, 20, 30].map((y) => (
                      <option key={y} value={y}>{y === 0 ? "Keep forever" : `Retain ${y} yr`}</option>
                    ))}
                  </select>
                )}
                {canDelete && !active.destroyed && active.destroy_on
                  && active.destroy_on <= new Date().toISOString().slice(0, 10) && (
                  <Button size="sm" onClick={logDestruction} disabled={!!working}
                          data-testid="archive-destroy-button"
                          className="bg-red-700 hover:bg-red-800">
                    {working === "destroy" ? <Loader2 size={14} className="mr-2 animate-spin" />
                      : <ShieldCheck size={14} className="mr-2" />}
                    Log paper destruction
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="ghost" onClick={() => remove(active.id)}
                          data-testid="archive-delete-button" className="text-red-600">
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>

              {active.verification !== "Verified" && !!active.transcript && (                <p className="flex items-start gap-2 border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-900"
                   data-testid="archive-unverified-warning">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  AI transcription — read it against the scan before relying on it clinically.
                  Uncertain words are marked [?].
                </p>
              )}

              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Original scan ({active.page_count})
                  </p>
                  <div className="space-y-3">
                    {Array.from({ length: active.page_count }).map((_, i) => (
                      <img key={i} alt={`Page ${i + 1}`} data-testid={`archive-page-${i}`}
                           src={`${process.env.REACT_APP_BACKEND_URL}/api/archive/documents/${active.id}/pages/${i}`}
                           className="w-full border border-slate-300 bg-white" />
                    ))}
                    {!active.page_count && (
                      <p className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                        No pages yet — add a scan or PDF.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Transcription
                  </p>
                  <Textarea rows={22} value={draft} onChange={(e) => setDraft(e.target.value)}
                            readOnly={!canVerify} data-testid="archive-transcript-textarea"
                            className="font-mono text-xs"
                            placeholder="Press “Read with AI”, or type the text in by hand." />
                  {canVerify && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => saveTranscript(null)} disabled={!!working}
                              data-testid="archive-save-transcript"
                              className="bg-slate-700 hover:bg-slate-800">
                        {working === "save" ? <Loader2 size={14} className="mr-2 animate-spin" />
                          : <FileText size={14} className="mr-2" />}
                        Save
                      </Button>
                      <Button size="sm" onClick={() => saveTranscript("Verified")} disabled={!!working}
                              data-testid="archive-verify-button"
                              className="bg-emerald-700 hover:bg-emerald-800">
                        <ShieldCheck size={14} className="mr-2" /> Save and verify
                      </Button>
                      <Button size="sm" variant="outline" disabled={!!working}
                              onClick={() => saveTranscript("Needs correction")}
                              data-testid="archive-needs-correction-button">
                        <X size={14} className="mr-2" /> Needs correction
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
